import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Search,
  Filter,
  FolderTree,
  Pencil,
  Trash2,
  Loader2,
  MoreVertical,
  Hash,
  Tag,
  Layers,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { apiFetch } from "@/lib/api";
import type {
  CoaCategoryDTO,
  CoaCategoryListResponse,
  CursorMeta,
  CoaType,
} from "@/types/chartOfAccount";
import { SettingsMenu } from "@/components/Holizontal/SettingsMenu";
import { RowActionMenu } from "@/components/RowActionMenu";

const API_PATH = "/settings/chart-of-account-categories";

const TYPES: (CoaType | "all")[] = [
  "all",
  "Assets",
  "Liabilities",
  "Equity",
  "Revenue",
  "Expenses",
];

const typeBadgeClass: Record<CoaType, string> = {
  Assets: "bg-primary/10 text-primary",
  Liabilities: "bg-destructive/10 text-destructive",
  Equity: "bg-chart-2/15 text-[hsl(199,89%,48%)]",
  Revenue: "bg-chart-4/15 text-[hsl(43,96%,40%)]",
  Expenses: "bg-muted text-foreground",
};

const isCoaType = (v: any): v is CoaType =>
  v === "Assets" || v === "Liabilities" || v === "Equity" || v === "Revenue" || v === "Expenses";

function cx(...s: Array<string | false | null | undefined>) {
  return s.filter(Boolean).join(" ");
}

const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
const isRTL = dir === "rtl";

/** ✅ MOBILE CARD */
function CategoryCard({
  row,
  saving,
  onEdit,
  onDelete,
}: {
  row: CoaCategoryDTO;
  saving: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="rounded-2xl shadow-sm overflow-hidden">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold truncate">{row.name}</div>

            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 text-xs">
                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">ID:</span>
                <span className="font-semibold">{row.id}</span>
              </span>

              <Badge variant="secondary" className={cx("rounded-full px-2 py-1 text-xs", typeBadgeClass[row.type])}>
                <Layers className="mr-1 h-3.5 w-3.5" />
                {row.type}
              </Badge>

              {row.name ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 text-xs">
                  <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Category</span>
                </span>
              ) : null}
            </div>
          </div>

          <RowActionMenu
            isRTL={isRTL}



          />
        </div>
      </CardContent>
    </Card>
  );
}

export default function CoaCategoriesPage() {
  // filters
  const [q, setQ] = useState("");
  const [type, setType] = useState<string>("all");

  // cursor meta
  const [meta, setMeta] = useState<CursorMeta>({
    per_page: 10,
    next_cursor: null,
    prev_cursor: null,
    has_more: false,
  });
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<string | number | null>(null);

  // data
  const [rows, setRows] = useState<CoaCategoryDTO[]>([]);
  const [loading, setLoading] = useState(false); // initial load
  const [loadingMore, setLoadingMore] = useState(false); // infinite scroll load
  const [saving, setSaving] = useState(false); // create/update/delete
  const [error, setError] = useState<string | null>(null);

  // dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const emptyForm = { id: "", name: "", type: "Expenses" as CoaType };
  const [form, setForm] = useState(emptyForm);

  // infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchRows = async (opts?: { cursor?: string | null; append?: boolean }) => {
    const append = !!opts?.append;

    if (append) setLoadingMore(true);
    else setLoading(true);

    setError(null);

    const params = new URLSearchParams();
    params.set("per_page", String(meta.per_page || 10));
    if (opts?.cursor) params.set("cursor", opts.cursor);
    if (q) params.set("q", q);
    params.set("type", type || "all");
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    try {
      const json = await apiFetch<CoaCategoryListResponse>(`${API_PATH}?${params.toString()}`);
      const newRows = json.data ?? [];

      setRows((prev) => {
        if (!append) return newRows;

        // ✅ avoid duplicates if API returns overlapping items
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev];
        for (const r of newRows) {
          if (!seen.has(r.id)) merged.push(r);
        }
        return merged;
      });

      setMeta((prev) => ({
        ...prev,
        per_page: json.meta?.per_page ?? prev.per_page ?? 10,
        next_cursor: json.meta?.next_cursor ?? null,
        prev_cursor: json.meta?.prev_cursor ?? null,
        has_more: json.meta?.has_more ?? false,
      }));

      setCursor(opts?.cursor ?? null);
      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load categories");
      if (!append) setRows([]);
      setMeta((m) => ({ ...m, next_cursor: null, prev_cursor: null, has_more: false }));
      setCursor(null);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  // initial load + on filter change (reset list)
  useEffect(() => {
    setRows([]);
    setCursor(null);
    setMeta((m) => ({ ...m, next_cursor: null, prev_cursor: null, has_more: false }));
    fetchRows({ cursor: null, append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, type]);

  // infinite scroll observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;

        if (loading || loadingMore || saving) return;
        if (!meta.next_cursor) return;

        fetchRows({ cursor: meta.next_cursor, append: true });
      },
      { root: null, rootMargin: "320px", threshold: 0 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [meta.next_cursor, loading, loadingMore, saving]);

  const openAdd = () => {
    setError(null);
    setForm({ id: "", name: "", type: "Expenses" });
    setAddOpen(true);
  };

  const openEdit = (row: CoaCategoryDTO) => {
    setError(null);
    setForm({ id: row.id, name: row.name, type: row.type });
    setEditOpen(true);
  };

  const createRow = async () => {
    const payload = { name: form.name.trim(), type: form.type };
    if (!payload.name) return setError("Name is required");
    if (!isCoaType(payload.type)) return setError("Type is required");

    setSaving(true);
    setError(null);
    try {
      await apiFetch(`${API_PATH}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setAddOpen(false);
      setRows([]);
      setCursor(null);
      await fetchRows({ cursor: null, append: false });
    } catch (e: any) {
      setError(e?.message ?? "Failed to create category");
    } finally {
      setSaving(false);
    }
  };

  const updateRow = async () => {
    const payload = { name: form.name.trim(), type: form.type };
    if (!form.id) return;
    if (!payload.name) return setError("Name is required");
    if (!isCoaType(payload.type)) return setError("Type is required");

    setSaving(true);
    setError(null);
    try {
      await apiFetch(`${API_PATH}/${form.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      setEditOpen(false);

      setRows((prev) => prev.map((r) => (r.id === form.id ? { ...r, name: payload.name, type: payload.type } : r)));
    } catch (e: any) {
      setError(e?.message ?? "Failed to update category");
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (id: string) => {
    if (!window.confirm("Delete this category?")) return;

    setSaving(true);
    setError(null);
    try {
      await apiFetch(`${API_PATH}/${id}`, { method: "DELETE" });
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e: any) {
      setError(e?.message ?? "Failed to delete category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <FolderTree className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">Chart of Account Categories</h1>
              <p className="text-xs text-muted-foreground">Infinite scroll + CRUD</p>
            </div>
          </div>

          <Button className="gap-2" onClick={openAdd} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}

          </Button>
        </div>
      </header>

      <SettingsMenu />

      <main className="container mx-auto px-4 py-6 space-y-4">
        {/* filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search category..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>

          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[220px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t === "all" ? "All Types" : t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <Card className="border-destructive/30">
            <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Categories</CardTitle>
            <CardDescription>Scroll down to load more</CardDescription>
          </CardHeader>

          <CardContent>
            {/* ✅ DESKTOP TABLE */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-[160px]">Type</TableHead>
                    <TableHead className="w-[90px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        No categories found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.id} className="hover:bg-muted/40">
                        <TableCell className="font-mono text-xs text-muted-foreground">{r.id}</TableCell>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={typeBadgeClass[r.type]}>
                            {r.type}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                disabled={saving}
                                className="h-10 w-12 rounded-xl border border-border bg-muted/30 hover:bg-muted/60"
                              >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
                              </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onClick={() => openEdit(r)} disabled={saving}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => deleteRow(r.id)}
                                disabled={saving}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}

                  {!loading && rows.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-6 text-center text-muted-foreground">
                        {loadingMore ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading more...
                          </span>
                        ) : meta.next_cursor ? (
                          <span className="text-xs">Scroll to load more</span>
                        ) : (
                          <span className="text-xs">End of list</span>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* ✅ MOBILE CARDS */}
            <div className="md:hidden space-y-3">
              {loading ? (
                <div className="rounded-xl border border-border p-6 text-center text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </span>
                </div>
              ) : rows.length === 0 ? (
                <div className="rounded-xl border border-border p-6 text-center text-sm text-muted-foreground">
                  No categories found.
                </div>
              ) : (
                rows.map((r) => (
                  <CategoryCard
                    key={r.id}
                    row={r}
                    saving={saving}
                    onEdit={() => openEdit(r)}
                    onDelete={() => deleteRow(r.id)}
                  />
                ))
              )}

              {/* Mobile bottom status */}
              {!loading && rows.length > 0 && (
                <div className="rounded-xl border border-border p-4 text-center text-xs text-muted-foreground">
                  {loadingMore ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading more...
                    </span>
                  ) : meta.next_cursor ? (
                    <span>Scroll to load more</span>
                  ) : (
                    <span>End of list</span>
                  )}
                </div>
              )}
            </div>

            {/* sentinel stays OUTSIDE table so it works for both desktop & mobile */}
            <div ref={sentinelRef} className="h-1 w-full" />
          </CardContent>
        </Card>
      </main>

      {/* ADD DIALOG */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Operating Expenses"
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v as CoaType }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.filter((t) => t !== "all").map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setAddOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button className="flex-1 gap-2" onClick={createRow} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v as CoaType }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.filter((t) => t !== "all").map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>


          </div>
          <div className="border-t border-border px-5 py-4">
            <DialogFooter className="gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button className="flex-1 gap-2" onClick={updateRow} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}